import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { tagService } from "../shared/api";
import { companyService } from "../shared/api/companyService";
import { contactAttributeKeys } from "../shared/api/contactAttributeService";
import { contactFilterService } from "../shared/api/contactFilterService";
import { contactService } from "../shared/api/contactService";
import { dataFieldService } from "../shared/api/dataFieldService";
import { TagFormValues, TagIndexResource, TagSaveRequest } from "../shared/types";
import { TagCreateDrawer, TagUpdateDrawer } from "./TagDrawer";

const mapValuesToRequest = (tag: TagFormValues): TagSaveRequest => {
	return {
		categoryId: tag.category?.id || null,
		translations: tag.translations,
	};
};

export const TagList: FC = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const canCreate = useHasPermission<PermissionName>(["tag.create"]);
	const canUpdate = useHasPermission<PermissionName>(["tag.update"]);
	const crudOperations = useLaravelCrudEntity(
		tagService,
		{
			entityName: t("Tag"),
			messages: {
				create: t("Tag was successfully created."),
				update: t("Tag was successfully updated."),
				delete: t("Tag was successfully deleted."),
			},
		},
		[
			contactService.listKey,
			contactAttributeKeys.index,
			dataFieldService.indexAllKey,
			contactFilterService.indexKey,
			companyService.listKey,
			tagService.optionsKey,
		],
		[tagService.quickFilterKey],
	);

	const dataListColumns: IDrawerDataListColumn<TagIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit tag")}
							label={t("Edit tag")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => crudOperations.deleteEntity(id)}
							title={t("Delete tag")}
							label={t("Delete tag")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this tag?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[crudOperations, t],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(tagService.listKey, tagService.index)}
			renderCreateDrawer={(drawerProps) => (
				<TagCreateDrawer
					{...drawerProps}
					onSubmit={async (entityFormState) => {
						const result = await crudOperations.createEntity(mapValuesToRequest(entityFormState));
						await queryClient.resetQueries({ queryKey: companyService.indexKey });
						await queryClient.resetQueries({ queryKey: contactService.indexKey });
						return result;
					}}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<TagUpdateDrawer
					{...drawerProps}
					onSubmit={async (entityFormState) => {
						const result = await crudOperations.updateEntity(drawerProps.id, mapValuesToRequest(entityFormState));
						await queryClient.resetQueries({ queryKey: companyService.indexKey });
						await queryClient.resetQueries({ queryKey: contactService.indexKey });
						return result;
					}}
				/>
			)}
			translations={{
				title: t("Tags"),
				empty: t("No tags available"),
			}}
			initialSearch={{ search: "", searchFields: [{ name: "tag_translations.name", label: t("Name") }] }}
		/>
	);
};
